import type { MaybeRefOrGetter } from "vue";
import { computed, ref, toValue } from "vue";

import { scrollNearBoarder } from "@/components/utils/scroll";
import { clientCoord } from "@/utils/dom/coordinates";
import { asReadonly } from "@/utils/vue";

type LinkDragKind = "sticky" | "objective";

export interface LinkDragRef {
  kind: LinkDragKind;
  boardId: string;
  id: string;
}

export interface LinkDragOptions {
  color: string;
  // Async rejections captured via Sentry; callers don't await.
  onDrop: (source: LinkDragRef, target: LinkDragRef) => void | Promise<void>;
  onStart?: (source: LinkDragRef) => void;
  onDragEnd?: (source: LinkDragRef, dropped: boolean) => void;
  // Anchor at this element's center; LinkDragLine masks its rect out.
  anchorEl?: () => HTMLElement | null;
}

interface DragState {
  anchor: { x: number; y: number };
  sourceRect?: { left: number; top: number; right: number; bottom: number };
  pointer: { x: number; y: number };
  color: string;
}

const _source = ref<LinkDragRef | null>(null);
const _target = ref<LinkDragRef | null>(null);
const _state = ref<DragState | null>(null);

export const linkDragSource = asReadonly(_source);
/** @internal exported for tests */
export const linkDragTarget = asReadonly(_target);
export const linkDragState = asReadonly(_state);
export const isLinkDragging = computed(() => _source.value !== null);

function refsEqual(a: LinkDragRef, b: LinkDragRef): boolean {
  return a.kind === b.kind && a.boardId === b.boardId && a.id === b.id;
}

function rectSnapshot(r: DOMRect) {
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

export function startLinkDrag(event: PointerEvent, source: LinkDragRef, options: LinkDragOptions) {
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  _source.value = { ...source };
  _target.value = null;
  options.onStart?.(source);

  const handleEl = event.currentTarget as HTMLElement;
  let sourceRect: DOMRect | null = null;
  let staticAnchor: { x: number; y: number } | null = null;

  function captureSource() {
    const el = options.anchorEl?.();
    if (el) {
      sourceRect = el.getBoundingClientRect();
    } else if (!staticAnchor) {
      const r = handleEl.getBoundingClientRect();
      staticAnchor = { x: r.left + r.width / 2, y: r.top };
    }
  }

  function anchorFromSource(): { x: number; y: number } {
    if (sourceRect) {
      return {
        x: (sourceRect.left + sourceRect.right) / 2,
        y: (sourceRect.top + sourceRect.bottom) / 2,
      };
    }
    return staticAnchor ?? { x: 0, y: 0 };
  }

  const startPointer = { x: event.clientX, y: event.clientY };
  captureSource();
  _state.value = {
    anchor: anchorFromSource(),
    sourceRect: sourceRect ? rectSnapshot(sourceRect) : undefined,
    pointer: startPointer,
    color: options.color,
  };

  // Idempotency guard for pointerup + pointercancel both firing.
  let ended = false;

  // Re-capture per frame so the mask tracks CSS transitions (e.g. sticky shrink).
  if (options.anchorEl) {
    const trackRect = () => {
      if (ended || !_state.value) return;
      captureSource();
      _state.value = {
        ..._state.value,
        anchor: anchorFromSource(),
        sourceRect: sourceRect ? rectSnapshot(sourceRect) : undefined,
      };
      requestAnimationFrame(trackRect);
    };
    requestAnimationFrame(trackRect);
  }

  function onMove(e: PointerEvent) {
    if (!_state.value) return;
    scrollNearBoarder(clientCoord(e));
    _state.value = {
      ..._state.value,
      pointer: { x: e.clientX, y: e.clientY },
    };
  }
  function end(canceled: boolean) {
    if (ended) return;
    ended = true;
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
    document.removeEventListener("pointercancel", onCancel);

    const from = _source.value;
    const to = canceled ? null : _target.value;
    const dropped = !!(from && to && !refsEqual(from, to));
    if (dropped && from && to) {
      void options.onDrop(from, to);
    }
    if (from) options.onDragEnd?.(from, dropped);

    // Line state stays alive for the cancel bounce-back.
    _source.value = null;
    _target.value = null;

    if (dropped) {
      _state.value = null;
    } else {
      animateLinePointerToAnchor();
    }
  }
  function onUp() {
    end(false);
  }
  function onCancel() {
    end(true);
  }

  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onUp);
  document.addEventListener("pointercancel", onCancel);
}

// Bails if a new drag takes over mid-animation.
function animateLinePointerToAnchor() {
  const start = _state.value;
  if (!start) return;
  const startPointer = { ...start.pointer };
  const { anchor } = start;
  const durationMs = 200;
  const startTime = performance.now();

  function step() {
    if (_source.value !== null) return;
    const s = _state.value;
    if (!s) return;
    const elapsed = performance.now() - startTime;
    const t = Math.min(1, elapsed / durationMs);
    const eased = 1 - Math.pow(1 - t, 3);
    _state.value = {
      ...s,
      pointer: {
        x: startPointer.x + (anchor.x - startPointer.x) * eased,
        y: startPointer.y + (anchor.y - startPointer.y) * eased,
      },
    };
    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      _state.value = null;
    }
  }

  requestAnimationFrame(step);
}

export function useLinkDragSource(source: MaybeRefOrGetter<LinkDragRef>, options: LinkDragOptions) {
  const handleProps = computed(() => ({ source: toValue(source), options }));
  return { handleProps };
}

export function useLinkDropTarget(
  target: MaybeRefOrGetter<LinkDragRef>,
  options: { isValid?: (source: LinkDragRef, target: LinkDragRef) => boolean } = {},
) {
  const isValidTarget = computed(() => {
    const src = linkDragSource.value;
    if (!src) return false;
    const t = toValue(target);
    if (refsEqual(src, t)) return false;
    return options.isValid ? options.isValid(src, t) : true;
  });

  const isDropTarget = computed(() => {
    const t = linkDragTarget.value;
    if (!t) return false;
    return refsEqual(t, toValue(target));
  });

  function onPointerEnter() {
    if (isValidTarget.value) _target.value = { ...toValue(target) };
  }

  function onPointerLeave() {
    if (isDropTarget.value) _target.value = null;
  }

  return { isDropTarget, onPointerEnter, onPointerLeave };
}
